import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AnimatedSection from './AnimatedSection';
import SectionIntro from './SectionIntro';
import { getPortfolio, PortfolioItem } from '../services/portfolio';

/** How many projects load at a time, both on first paint and on "See more". */
const PAGE_SIZE = 4;

/**
 * Shown only if the portfolio API is unreachable, so the Work section is never
 * empty. Once the API has items these are ignored entirely.
 */
const FALLBACK_PROJECTS: PortfolioItem[] = [
  {
    id: 'zoxohu',
    title: 'Zoxohu',
    category: 'Hospitality Operating System',
    description:
      'POS, inventory, customers, accounting, and analytics in one operating layer for hospitality and commerce.',
    type: 'video',
    media: '/zoxo.mp4',
    websiteUrl: 'https://zoxohu.com',
    featured: true,
  },
  {
    id: 'isemma',
    title: 'ISEMMA',
    category: 'Wine & Celebration Commerce',
    description:
      'A premium wine and celebration commerce platform — luxury retail, gifting, corporate orders, and curated collections.',
    type: 'video',
    media: '/isemma.mp4',
    websiteUrl: 'https://www.isemma.com',
    featured: true,
  },
];

const ExternalLink: React.FC<{ href?: string; className?: string; children: React.ReactNode }> = ({
  href,
  className = '',
  children,
}) => {
  if (!href || href === '#') return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
};

/** Strips the scheme and any trailing slash so the frame reads like an address bar. */
const displayHost = (url?: string): string => {
  if (!url || url === '#') return '';
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

/** Matches Tailwind's `lg` breakpoint, where the gallery switches to the desktop layout. */
const DESKTOP_QUERY = '(min-width: 1024px)';

/**
 * Tracks whether the desktop layout applies. Only one layout is mounted at a time,
 * so a phone never downloads the desktop gallery's videos (and vice versa) —
 * hiding the other layout with CSS alone still fetched its media.
 */
const useIsDesktop = (): boolean => {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
};

/** Media preview for a project — video plays only while it is the active card. */
const ProjectMedia: React.FC<{
  project: PortfolioItem;
  active: boolean;
  videoRef?: (el: HTMLVideoElement | null) => void;
}> = ({ project, active, videoRef }) => {
  if (project.type === 'video') {
    return (
      <video
        ref={videoRef}
        src={project.media}
        muted
        loop
        playsInline
        // Inactive videos still fetch their first frame, so nothing shows as a
        // black box while it waits its turn.
        preload={active ? 'auto' : 'metadata'}
        autoPlay={active}
      />
    );
  }

  if (project.media) {
    return <img src={project.media} alt={project.title} loading="lazy" />;
  }

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-surface-secondary">
      <p className="font-display text-6xl xl:text-7xl font-semibold text-white/10 tracking-tighter select-none">
        {project.title}
      </p>
    </div>
  );
};

/**
 * Wraps the media in our own browser chrome. The recordings already contain the
 * capture machine's tab strip and URL bar, so `.media-crop` slices that off and
 * this frame replaces it with something deliberate.
 */
const ProjectFrame: React.FC<{
  project: PortfolioItem;
  active: boolean;
  className?: string;
  stageClassName?: string;
  videoRef?: (el: HTMLVideoElement | null) => void;
}> = ({ project, active, className = '', stageClassName = '', videoRef }) => (
  <div className={`project-frame ${className}`}>
    <div className="project-frame__bar">
      <span className="project-frame__dot" />
      <span className="project-frame__dot" />
      <span className="project-frame__dot" />
      <span className="project-frame__address">{displayHost(project.websiteUrl) || project.title}</span>
    </div>
    <div className={`project-frame__stage media-crop ${stageClassName}`}>
      <ProjectMedia project={project} active={active} videoRef={videoRef} />
    </div>
  </div>
);

const Ecosystem: React.FC = () => {
  const [projects, setProjects] = useState<PortfolioItem[]>([]);
  const [active, setActive] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [caseStudy, setCaseStudy] = useState<PortfolioItem | null>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const mobileVideoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const isDesktop = useIsDesktop();

  const loadPage = useCallback(async (targetPage: number) => {
    const response = await getPortfolio({ limit: PAGE_SIZE, page: targetPage });
    setTotalPages(response.pagination.pages || 1);
    setProjects((prev) => {
      const merged = targetPage === 1 ? response.data : [...prev, ...response.data];
      // The API sorts by `featured`, so a page boundary can repeat an item.
      return merged.filter((item, i, all) => all.findIndex((p) => p.id === item.id) === i);
    });
    setPage(targetPage);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await getPortfolio({ limit: PAGE_SIZE, page: 1 });
        if (cancelled) return;
        if (response.data.length) {
          setProjects(response.data);
          setTotalPages(response.pagination.pages || 1);
        } else {
          setProjects(FALLBACK_PROJECTS);
          setTotalPages(1);
        }
      } catch (err) {
        console.warn('Failed to load portfolio, using fallback projects:', err);
        if (!cancelled) {
          setProjects(FALLBACK_PROJECTS);
          setTotalPages(1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSeeMore = async () => {
    setLoadingMore(true);
    try {
      await loadPage(page + 1);
    } catch (err) {
      console.warn('Failed to load more projects:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const selectProject = (index: number) => {
    setActive(index);
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === index) {
        v.play().catch(() => undefined);
      } else {
        v.pause();
        v.currentTime = 0;
      }
    });
  };

  /**
   * Mobile has no hover to drive the preview, so each card plays while it is on
   * screen and pauses when it leaves. The `autoPlay` attribute alone only ever
   * covered the first two cards, and iOS ignores it for anything off screen —
   * hence an observer calling play() directly. Muted + playsInline (set on the
   * element) are what make unattended playback legal on iOS and Android.
   */
  useEffect(() => {
    if (isDesktop || typeof IntersectionObserver === 'undefined') return;

    const videos = mobileVideoRefs.current.filter((v): v is HTMLVideoElement => Boolean(v));
    if (!videos.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement;
          if (entry.isIntersecting) {
            // Rejects when the browser declines (iOS Low Power Mode, data saver);
            // the poster frame stays up, which is the graceful outcome.
            video.play().catch(() => undefined);
          } else {
            video.pause();
          }
        });
      },
      // Start a little before the card scrolls in, so it is already moving on arrival.
      { threshold: 0, rootMargin: '200px 0px' }
    );

    videos.forEach((v) => observer.observe(v));
    return () => observer.disconnect();
  }, [projects, isDesktop]);

  // Close the case study on Escape and stop the page scrolling behind it.
  useEffect(() => {
    if (!caseStudy) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCaseStudy(null);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [caseStudy]);

  const hasMore = page < totalPages;

  return (
    <section id="work" className="section-pad relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <SectionIntro
          label="Our Work"
          title={<>Platforms we&apos;ve built for hospitality and commerce.</>}
          body="Real operating systems and commerce platforms — designed around how these businesses actually run."
          aside="Select a project to explore the platform behind the work."
          className="mb-14 md:mb-20"
        />

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 xl:gap-14 min-h-[420px] animate-pulse">
            <div className="lg:col-span-5 space-y-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-20 bg-surface-secondary rounded-lg" />
              ))}
            </div>
            <div className="lg:col-span-7 bg-surface-secondary rounded-lg min-h-[320px]" />
          </div>
        ) : (
          <>
            {/* Desktop: interactive gallery */}
            {isDesktop ? (
              <div className="grid grid-cols-12 gap-10 xl:gap-14 items-stretch min-h-[520px]">
                {/* Project index */}
                <div className="col-span-5 flex flex-col justify-center">
                  <div className="space-y-1">
                    {projects.map((p, i) => (
                      <button
                        key={p.id}
                        type="button"
                        onMouseEnter={() => selectProject(i)}
                        onFocus={() => selectProject(i)}
                        onClick={() => selectProject(i)}
                        className={`group w-full text-left py-5 border-b border-surface-border transition-all duration-500 ${
                          i === active ? 'border-primary-500/30' : 'hover:border-surface-hover'
                        }`}
                      >
                        <div className="flex items-baseline justify-between gap-4">
                          <div className="min-w-0">
                            <span
                              className={`text-xs tracking-widest uppercase mb-2 block transition-colors duration-300 ${
                                i === active ? 'text-primary-500' : 'text-muted/50'
                              }`}
                            >
                              {p.category || 'Project'}
                              {p.featured ? ' · Featured' : ''}
                            </span>
                            <h3
                              className={`font-display text-3xl xl:text-4xl font-semibold tracking-tight transition-colors duration-300 ${
                                i === active ? 'text-white' : 'text-white/40 group-hover:text-white/70'
                              }`}
                            >
                              {p.title}
                            </h3>
                          </div>
                          <span
                            className={`text-2xl font-display transition-all duration-300 shrink-0 ${
                              i === active
                                ? 'text-primary-500 translate-x-0 opacity-100'
                                : 'text-muted/20 -translate-x-2 opacity-0 group-hover:opacity-40'
                            }`}
                            aria-hidden
                          >
                            →
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview stage */}
                <div className="col-span-7 relative">
                  <AnimatedSection animation="fade-in" className="h-full">
                    <div className="relative h-full min-h-[600px]">
                      {projects.map((p, i) => (
                        <div
                          key={p.id}
                          className={`absolute inset-0 flex flex-col gap-6 transition-opacity duration-700 ${
                            i === active ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
                          }`}
                        >
                          <ProjectFrame
                            project={p}
                            active={i === active}
                            videoRef={(el) => {
                              videoRefs.current[i] = el;
                            }}
                          />

                          <div className="shrink-0">
                            <p className="text-muted text-sm md:text-base leading-relaxed max-w-lg mb-5 line-clamp-3">
                              {p.description}
                            </p>
                            <div className="flex flex-wrap items-center gap-4">
                              {p.description && (
                                <button
                                  type="button"
                                  onClick={() => setCaseStudy(p)}
                                  className="btn-secondary text-sm py-2 px-4"
                                >
                                  Case Study
                                </button>
                              )}
                              <ExternalLink
                                href={p.websiteUrl}
                                className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
                              >
                                View project
                                <span aria-hidden>→</span>
                              </ExternalLink>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </AnimatedSection>
                </div>
              </div>
            ) : (
              /* Mobile: stacked project strips — bare video, no browser frame */
              <div className="space-y-px bg-surface-border">
                {projects.map((p, i) => (
                  <AnimatedSection key={p.id} animation="slide-up" delay={Math.min(i, 3) * 60}>
                    <article className="bg-surface">
                      {/* The first couple load in full and autoplay straight away; the
                          observer above starts the rest as they scroll in. */}
                      <div className="media-crop bg-black">
                        <ProjectMedia
                          project={p}
                          active={i < 2}
                          videoRef={(el) => {
                            mobileVideoRefs.current[i] = el;
                          }}
                        />
                      </div>
                      <div className="p-6">
                        {p.category && (
                          <span className="block text-xs text-primary-500 uppercase tracking-widest mb-3">
                            {p.category}
                          </span>
                        )}
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <h3 className="font-display text-2xl font-semibold text-white">{p.title}</h3>
                          {p.featured && (
                            <span className="text-[10px] uppercase tracking-wider text-muted border border-surface-border px-2 py-1 shrink-0">
                              Featured
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted leading-relaxed mb-5 line-clamp-3">{p.description}</p>
                        <div className="flex flex-wrap items-center gap-4">
                          {p.description && (
                            <button
                              type="button"
                              onClick={() => setCaseStudy(p)}
                              className="btn-secondary text-sm py-2 px-4"
                            >
                              Case Study
                            </button>
                          )}
                          <ExternalLink
                            href={p.websiteUrl}
                            className="text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
                          >
                            View project →
                          </ExternalLink>
                        </div>
                      </div>
                    </article>
                  </AnimatedSection>
                ))}
              </div>
            )}

            {hasMore && (
              <div className="mt-12 md:mt-16 flex justify-center">
                <button
                  type="button"
                  onClick={handleSeeMore}
                  disabled={loadingMore}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMore ? 'Loading…' : 'See more work'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Case study */}
      {caseStudy &&
        createPortal(
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in"
            role="dialog"
            aria-modal="true"
            aria-label={`${caseStudy.title} case study`}
            onClick={() => setCaseStudy(null)}
          >
            <div
              className="relative flex w-full max-w-3xl max-h-[90vh] flex-col bg-surface-card border border-surface-border rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-none items-start justify-between gap-4 p-6 border-b border-surface-border">
                <div className="min-w-0">
                  <p className="text-xs text-primary-500 uppercase tracking-widest mb-2">
                    {caseStudy.category || 'Case Study'}
                  </p>
                  <h3 className="font-display text-2xl font-semibold text-white">{caseStudy.title}</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setCaseStudy(null)}
                  className="p-2 hover:bg-surface-hover rounded-lg transition-colors duration-300 shrink-0"
                  aria-label="Close case study"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {caseStudy.media && (
                  <div className="media-crop bg-black">
                    {caseStudy.type === 'video' ? (
                      <video src={caseStudy.media} controls autoPlay muted loop playsInline />
                    ) : (
                      <img src={caseStudy.media} alt={caseStudy.title} />
                    )}
                  </div>
                )}
                <p className="p-6 text-muted leading-relaxed whitespace-pre-line">
                  {caseStudy.description}
                </p>
              </div>

              <div className="flex flex-none items-center justify-end gap-3 p-6 border-t border-surface-border">
                <button type="button" onClick={() => setCaseStudy(null)} className="btn-ghost text-sm">
                  Close
                </button>
                <ExternalLink href={caseStudy.websiteUrl} className="btn-primary text-sm py-2 px-4">
                  View project
                  <span aria-hidden>→</span>
                </ExternalLink>
              </div>
            </div>
          </div>,
          document.body
        )}
    </section>
  );
};

export default Ecosystem;
